/* eslint-disable prettier/prettier */
import axiosInstance from './axios/axiosConfig'
import { endpoints } from './axios/endpoints'

const silent = { silentToast: true }
// A dry run sent as POST (it enqueues and stores nothing): the caller shows
// its own pending state, so it does not cover the screen.
const dryRun = { silentToast: true, background: true }

/**
 * Per-domain sending warmup, plus the pre-send report and segment preview.
 *
 * All three back onto endpoints that are already live in
 * CommunicationManagementService. Nothing here invents behaviour -- the
 * response shapes below are what the API returns today.
 *
 * On warmup: we send from the SES shared IP pool, which AWS keeps warm, so
 * there is no IP to warm. What the ramp builds is DOMAIN reputation, which is
 * a function of who we send to and in what order. That is why every step
 * carries a min_engagement_days alongside its cap.
 */
const SendingDomainService = {
    /**
     * List registered sending domains.
     *
     * Each row is the stored config plus live ramp position computed
     * server-side: current_day, current_cap, current_min_engagement_days,
     * sent_today. The UI does not recompute any of that.
     *
     * @param {string} status - optional filter: warming | active | paused
     */
    list: (status = '') =>
        axiosInstance.get(
            status
                ? `${endpoints.sendingDomains}?status=${encodeURIComponent(status)}`
                : endpoints.sendingDomains
        ),

    get: (domain) => axiosInstance.get(endpoints.sendingDomain(domain)),

    /**
     * Register or replace a domain's warmup configuration.
     *
     * Omit `ramp` to accept the server default, which encodes the practice
     * worth keeping: no step more than doubles per elapsed day, and the
     * engagement window widens as reputation builds. A custom ramp is
     * validated server-side and rejected with a readable reason.
     */
    register: (payload) => axiosInstance.post(endpoints.sendingDomains, payload),

    /** status: warming | active | paused. Pausing blocks sends from the domain. */
    setStatus: (domain, status) =>
        axiosInstance.post(endpoints.sendingDomainStatus, { domain, status }),

    /**
     * Assess a prospective send against a domain's ramp.
     *
     * Accepts either `domain` or a `sender` From address, since callers
     * usually hold the latter from the template's source_email.
     *
     * Advisory by design: a cap breach returns would_exceed=true with
     * blocked=false, so a mis-set ramp can never strand a campaign. Only a
     * paused domain blocks.
     */
    evaluate: ({ domain = '', sender = '', recipientCount = 0 }) =>
        axiosInstance.post(
            endpoints.sendingDomainEvaluate,
            { domain, sender, recipient_count: recipientCount },
            dryRun
        ),
}

/**
 * Dry-run a bulk send. Enqueues nothing.
 *
 * Built from the same payload /v2/bulk_comms receives, so the recipient count
 * shown is the count that would actually be enqueued rather than a separate
 * derivation that can drift from it.
 */
export const PreSendService = {
    report: ({
        eventType,
        eventId = '',
        identities,
        others = {},
        sender = '',
        templateBody = '',
        subject = '',
        channels = ['email'],
        // The rest of the send payload, so the report describes the same
        // send: a scheduled moment and zone, and the linked variables.
        timezone,
        eventTime = '',
        mappedOthers = {},
        sessionId = '',
    }) =>
        axiosInstance.post(
            endpoints.preSendReport,
            {
                event_type: eventType,
                event_id: eventId,
                identities,
                others,
                sender,
                template_body: templateBody,
                subject,
                channels,
                ...(timezone ? { timezone } : {}),
                event_time: eventTime,
                mapped_others: mappedOthers,
                session_id: sessionId,
            },
            dryRun
        ),
}

/**
 * Cross-channel segmentation: one audience into N mutually exclusive cohorts,
 * each with its own channel and template.
 *
 * Preview only. The operator reviews cohort counts and per-cohort reports,
 * then dispatches each returned payload through the existing bulk send -- so a
 * segmentation mistake is a wasted preview rather than a wrong send.
 */
export const SegmentService = {
    /** Rule operators, channels and match modes the server accepts. */
    operators: () => axiosInstance.get(endpoints.segmentOperators),

    /**
     * @param {Array} segments - [{name, channel, template_id, rules[], match}]
     *   Evaluated in declared order; the first match claims each recipient,
     *   which is what makes cohorts exclusive. A rule-less cohort is a
     *   catch-all and is only valid last.
     */
    preview: ({
        eventType,
        eventId = '',
        identities,
        others = {},
        segments,
        sender = '',
        templateBodies = {},
        subjects = {},
        includeRows = false,
    }) =>
        axiosInstance.post(
            endpoints.segmentsPreview,
            {
                event_type: eventType,
                event_id: eventId,
                identities,
                others,
                segments,
                sender,
                template_bodies: templateBodies,
                subjects,
                include_rows: includeRows,
            },
            dryRun
        ),
}

/**
 * Sending reputation.
 *
 * Two different questions, and the distinction is the point of the module:
 *
 *   account()   -- where we stand with AWS. Enforcement is account-wide, so
 *                  this is the number that can suspend OTPs and receipts.
 *   campaigns() -- which campaigns own the complaints. The account average can
 *                  sit comfortably under 0.1% while individual campaigns run
 *                  several times that; those are what drag it up as volume
 *                  grows.
 *
 * Rates are percentages. A null rate means unreadable, NOT zero -- render it
 * as unknown, never as clean.
 */
export const ReputationService = {
    /** @param {boolean} refresh - bypass the server-side cache */
    account: (refresh = false) =>
        axiosInstance.get(
            refresh
                ? `${endpoints.reputationAccount}?refresh=true`
                : endpoints.reputationAccount,
            silent
        ),

    /**
     * Campaigns ranked by complaint rate, worst first.
     *
     * Costs one DynamoDB query per campaign examined, hence `limit`. Rows
     * carry `sampled: true` when the per-campaign row cap was hit, so a
     * partial count is never read as a complete one.
     */
    campaigns: ({
        days = 30,
        limit = 40,
        maxRows = 2000,
        overThresholdOnly = false,
    } = {}) =>
        axiosInstance.get(
            `${endpoints.reputationCampaigns}?days=${days}&limit=${limit}` +
                `&max_rows=${maxRows}&over_threshold_only=${overThresholdOnly}`,
            silent
        ),
}

export default SendingDomainService
